import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TaskEvent } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_TASK_EVENTS_QUERY_KEY } from './tasks-query-keys';

export function useFetchTaskEvents(taskId: string): {
    status: 'error' | 'success' | 'pending';
    events: TaskEvent[];
    refetch: () => void;
} {
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const { data, status, refetch } = useQuery({
        queryKey: [FETCH_TASK_EVENTS_QUERY_KEY, taskId],
        queryFn: () => {
            return openApiRef.current.tasks.getEvents(taskId).then((res) => {
                const items = res.data.data.events.data;
                // Cached as a Map so a pushed event can be written in by id —
                // see `addOrUpdateTaskEventsCache`.
                const map: Map<string, TaskEvent> = new Map(
                    items.map((item) => [item.id, item])
                );
                return map;
            });
        },
        // Nothing to read before a task has been chosen.
        enabled: taskId !== ''
    });

    const events = useMemo(() => {
        if (data) {
            return Array.from(data.values());
        } else {
            return [];
        }
    }, [data]);

    return {
        status,
        events,
        refetch: () => {
            void refetch();
        }
    };
}
